import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { getFirstEducationErrorRowIndex } from 'src/utils/resume-validation';
import { Input } from 'src/components/ui/input';
import { Button } from 'src/components/ui/button';
import { CalendarIcon, Trash2, AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useToast } from 'src/hooks/use-toast';
import { educations, boardOptions } from './constants';
import { AddButton } from './common/index';

const FormField = ({
  label,
  id,
  errorMessage,
  children,
  required = false,
  'data-field': dataField,
  className = '',
  validationActive = false,
  /** When children is a Controller, pass whether the controlled field has a non-empty value (for green outline). */
  fieldHasValue,
}) => {
  const hasErr = Boolean(errorMessage);
  const childProps = children.props || {};
  const val = childProps.value;
  let hasVal =
    val != null &&
    String(val).trim() !== '' &&
    !(typeof val === 'number' && Number.isNaN(val));
  if (typeof fieldHasValue === 'boolean') {
    hasVal = fieldHasValue;
  }
  const isValid = validationActive && !hasErr && hasVal;
  return (
    <div className={`edu-fg ${className}`} data-field={dataField}>
      <label htmlFor={id} className="edu-fl">
        {label}
        {required && <span className="edu-req">*</span>}
      </label>
      <div className="relative">
        {React.cloneElement(children, {
          id,
          'aria-labelledby': id,
          'data-field': dataField,
          className:
            `${childProps.className || ''} edu-fi ${hasErr ? 'edu-fi--error' : ''} ${
              isValid ? 'edu-fi--valid' : ''
            } text-xs`.trim(),
        })}
      </div>
      <div
        className={`edu-field-error-msg ${hasErr ? 'edu-field-error-msg--show' : ''}`}
      >
        {hasErr && (
          <>
            <AlertCircle strokeWidth={2} className="shrink-0" />
            {errorMessage}
          </>
        )}
      </div>
    </div>
  );
};

// Reusable Form Components
const FormSelect = ({
  name,
  control,
  options,
  placeholder,
  icon,
  disabled,
  rules = {},
  defaultValue,
  onChange,
  value,
  'data-field': dataField,
  id,
  triggerRef,
  className = '',
  label,
  required = false,
  validationActive = false,
}) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState: { error } }) => {
      const raw = value !== undefined && value !== null ? value : field.value;
      const selectValue =
        raw === '' || raw === undefined || raw === null
          ? undefined
          : String(raw);
      const hasErr = Boolean(error);
      const hasVal = selectValue != null && String(selectValue).trim() !== '';
      const isValid = validationActive && !hasErr && hasVal;
      const inner = (
        <div
          data-field={dataField || name}
          style={{ scrollMarginTop: '100px' }}
        >
          <Select
            onValueChange={(val) => {
              field.onChange(val);
              if (onChange) {
                onChange(val);
              }
            }}
            value={selectValue}
            disabled={disabled}
          >
            <SelectTrigger
              className={`edu-fi edu-fi-select-trigger flex h-auto min-h-[42px] w-full items-center border bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none ${
                hasErr ? 'edu-fi--error' : ''
              } ${isValid ? 'edu-fi--valid' : ''} ${className}`.trim()}
              id={id}
              ref={triggerRef}
            >
              <div className="flex items-center min-w-0 flex-1">
                {icon}
                <SelectValue placeholder={placeholder} />
              </div>
            </SelectTrigger>
            <SelectContent className="border border-ah-surface-card-border">
              {Array.isArray(options) &&
                options.map((option) => (
                  <SelectItem
                    key={option?.id || option?.value}
                    value={option?.value || option?.id?.toString()}
                    className="text-xs"
                  >
                    {option?.label || option?.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      );
      if (!label) return inner;
      return (
        <div className="edu-fg">
          <label htmlFor={id} className="edu-fl">
            {label}
            {required && <span className="edu-req">*</span>}
          </label>
          {inner}
          <div
            className={`edu-field-error-msg ${hasErr ? 'edu-field-error-msg--show' : ''}`}
          >
            {hasErr && (
              <>
                <AlertCircle strokeWidth={2} className="shrink-0" />
                {error.message}
              </>
            )}
          </div>
        </div>
      );
    }}
  />
);

const RadioButton = ({ id, value, checked, onChange, label, className }) => (
  <label
    htmlFor={id}
    className={`flex items-center gap-1.5 cursor-pointer text-[0.82rem] text-[var(--text)] font-[family-name:var(--font-body)] ${className}`}
  >
    <input
      type="radio"
      id={id}
      value={value}
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 accent-[var(--primary)] border-gray-300"
    />
    {label}
  </label>
);

const validateNewQualification = (
  allEducations,
  newQualification,
  currentIndex
) => {
  const otherEducations = allEducations.filter(
    (_, index) => index !== currentIndex
  );

  if (newQualification === 'TENTH') {
    const hasTenth = otherEducations.some(
      (edu) => edu.qualification === 'TENTH'
    );
    if (hasTenth) {
      return {
        isValid: false,
        message: 'Secondary qualification can only be added once',
      };
    }
  }

  if (newQualification === 'TWELFTH') {
    const hasTwelfth = otherEducations.some(
      (edu) => edu.qualification === 'TWELFTH'
    );
    if (hasTwelfth) {
      return {
        isValid: false,
        message: 'Higher Secondary qualification can only be added once',
      };
    }
  }

  return { isValid: true };
};

const getFieldOfStudyOptions = (qualification, isEnabled, masterData) => {
  if (!isEnabled) {
    return [];
  }

  // Secondary (10th) and Higher Secondary (12th) draw from the "Secondary
  // Education" list; every other qualification (Diploma, Bachelor's, Master's…)
  // draws from the "Higher Education" list.
  if (qualification === 'TENTH' || qualification === 'TWELFTH') {
    return masterData.fieldsofstudieshsc || [];
  }

  return masterData.fieldsofstudies || [];
};

// Utility functions
const generateGPAOptions = (maxGPA) => {
  if (!maxGPA) return [];
  const numMaxGPA = parseFloat(maxGPA);
  if (Number.isNaN(numMaxGPA)) return [];

  return Array.from({ length: Math.floor(numMaxGPA * 10) + 1 }, (_, i) => ({
    value: (i / 10).toFixed(1),
    label: (i / 10).toFixed(1),
  }));
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from(
    { length: currentYear - 1980 + 1 },
    (_, i) => currentYear - i
  )
    .reverse()
    .map((year) => ({
      value: year.toString(),
      label: year.toString(),
    }));
};

const formatGPAValue = (value) => {
  if (!value) return undefined;
  return value.includes('.') ? value : `${value}.0`;
};

// Field Components
const GradeInputs = ({
  type,
  index,
  control,
  watch,
  errors,
  maxMarksInputRef,
  earnedMarksInputRef,
  maxGPATriggerRef,
  earnedGPATriggerRef,
  validationActive = false,
}) => {
  const maxMarksValue = watch(`educations.${index}.maxMarks`);
  const earnedMarksValue = watch(`educations.${index}.earnedMarks`);
  const maxGPAValue = watch(`educations.${index}.maxGPA`);
  const earnedGPAValue = watch(`educations.${index}.earnedGPA`);
  const formattedMaxGPA = formatGPAValue(maxGPAValue);
  const formattedEarnedGPA = formatGPAValue(earnedGPAValue);

  if (type === 'marks') {
    const maxErr =
      errors?.educations?.[index]?.maxMarks?.message ||
      (earnedMarksValue &&
      maxMarksValue &&
      parseFloat(earnedMarksValue) > parseFloat(maxMarksValue)
        ? 'Score cannot exceed maximum marks'
        : '');
    const earnedErr =
      errors?.educations?.[index]?.earnedMarks?.message ||
      (earnedMarksValue &&
      maxMarksValue &&
      parseFloat(earnedMarksValue) > parseFloat(maxMarksValue)
        ? 'Score cannot exceed maximum marks'
        : '');
    return (
      <div className="edu-fg-row">
        <FormField
          label="Max Marks"
          id={`max-marks-${index}`}
          data-field={`max-marks-${index}`}
          errorMessage={maxErr}
          required
          validationActive={validationActive}
          fieldHasValue={
            maxMarksValue !== undefined &&
            maxMarksValue !== null &&
            String(maxMarksValue).trim() !== ''
          }
        >
          <Controller
            name={`educations.${index}.maxMarks`}
            data-field={`educations.${index}.maxMarks`}
            control={control}
            rules={{
              validate: (value, formValues) => {
                const education = formValues.educations[index];
                const hasAnyValue =
                  education.institution ||
                  education.qualification ||
                  education.graduationYear ||
                  education.fieldsOfStudy ||
                  education.board ||
                  education.university ||
                  education.maxMarks ||
                  education.earnedMarks ||
                  education.maxGPA ||
                  education.earnedGPA;

                if (hasAnyValue && type === 'marks' && !value) {
                  return 'Max marks is required';
                }

                return true;
              },
              min: 0,
            }}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                inputMode="decimal"
                min={0}
                onKeyDown={(e) => {
                  if (
                    e.key === '-' ||
                    e.key === '+' ||
                    e.key === 'e' ||
                    e.key === 'E'
                  )
                    e.preventDefault();
                }}
                ref={(node) => {
                  field.ref(node);
                  if (maxMarksInputRef) maxMarksInputRef.current = node;
                }}
                placeholder="Enter max marks"
                className={`edu-fi text-xs ${
                  maxErr
                    ? 'edu-fi--error'
                    : validationActive &&
                        maxMarksValue !== undefined &&
                        maxMarksValue !== null &&
                        String(maxMarksValue).trim() !== ''
                      ? 'edu-fi--valid'
                      : ''
                }`}
              />
            )}
          />
        </FormField>
        <FormField
          label="Scored Marks"
          id={`earned-marks-${index}`}
          data-field={`earned-marks-${index}`}
          errorMessage={earnedErr}
          required
          validationActive={validationActive}
          fieldHasValue={
            earnedMarksValue !== undefined &&
            earnedMarksValue !== null &&
            String(earnedMarksValue).trim() !== ''
          }
        >
          <Controller
            name={`educations.${index}.earnedMarks`}
            data-field={`educations.${index}.earnedMarks`}
            control={control}
            rules={{
              validate: (val, formValues) => {
                const education = formValues.educations[index];
                const hasAnyValue =
                  education.institution ||
                  education.qualification ||
                  education.graduationYear ||
                  education.fieldsOfStudy ||
                  education.board ||
                  education.university ||
                  education.maxMarks ||
                  education.earnedMarks ||
                  education.maxGPA ||
                  education.earnedGPA;

                if (hasAnyValue && type === 'marks' && !val) {
                  return 'Score is required';
                }

                const maxMarks = watch(`educations.${index}.maxMarks`);
                if (maxMarks && val && parseFloat(val) > parseFloat(maxMarks)) {
                  return 'Score cannot exceed maximum marks';
                }

                return true;
              },
              min: 0,
            }}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                inputMode="decimal"
                min={0}
                onKeyDown={(e) => {
                  if (
                    e.key === '-' ||
                    e.key === '+' ||
                    e.key === 'e' ||
                    e.key === 'E'
                  )
                    e.preventDefault();
                }}
                ref={(node) => {
                  field.ref(node);
                  if (earnedMarksInputRef) earnedMarksInputRef.current = node;
                }}
                placeholder="Enter scored marks"
                className={`edu-fi text-xs ${
                  earnedErr
                    ? 'edu-fi--error'
                    : validationActive &&
                        earnedMarksValue !== undefined &&
                        earnedMarksValue !== null &&
                        String(earnedMarksValue).trim() !== ''
                      ? 'edu-fi--valid'
                      : ''
                }`}
              />
            )}
          />
        </FormField>
      </div>
    );
  }

  const gpaOptions = [
    { value: '5.0', label: '5.0' },
    { value: '10.0', label: '10.0' },
  ];

  return (
    <div className="edu-fg-row">
      <FormSelect
        label="Max GPA"
        required
        validationActive={validationActive}
        name={`educations.${index}.maxGPA`}
        data-field={`educations.${index}.maxGPA`}
        control={control}
        options={gpaOptions}
        placeholder="Enter max GPA"
        value={formattedMaxGPA}
        id={`educations.${index}.maxGPA`}
        triggerRef={maxGPATriggerRef}
        rules={{
          validate: (value, formValues) => {
            const education = formValues.educations[index];
            const hasAnyValue =
              education.institution ||
              education.qualification ||
              education.graduationYear ||
              education.fieldsOfStudy ||
              education.board ||
              education.university ||
              education.maxMarks ||
              education.earnedMarks ||
              education.maxGPA ||
              education.earnedGPA;

            if (hasAnyValue && type === 'gpa' && !value) {
              return 'Max GPA is required';
            }

            return true;
          },
        }}
      />
      <FormSelect
        label="Scored GPA"
        required
        validationActive={validationActive}
        name={`educations.${index}.earnedGPA`}
        data-field={`educations.${index}.earnedGPA`}
        control={control}
        options={generateGPAOptions(maxGPAValue?.replace('.0', ''))}
        placeholder="Enter scored GPA"
        rules={{
          validate: (val, formValues) => {
            const education = formValues.educations[index];
            const hasAnyValue =
              education.institution ||
              education.qualification ||
              education.graduationYear ||
              education.fieldsOfStudy ||
              education.board ||
              education.university ||
              education.maxMarks ||
              education.earnedMarks ||
              education.maxGPA ||
              education.earnedGPA;

            if (hasAnyValue && type === 'gpa' && !val) {
              return 'Score is required';
            }

            const maxGPA = watch(`educations.${index}.maxGPA`);
            if (maxGPA && val && parseFloat(val) > parseFloat(maxGPA)) {
              return 'Scored GPA cannot exceed maximum GPA';
            }

            return true;
          },
        }}
        value={formattedEarnedGPA}
        id={`educations.${index}.earnedGPA`}
        triggerRef={earnedGPATriggerRef}
      />
    </div>
  );
};

const GradeTypeSelector = ({
  index,
  onGradeTypeChange,
  control,
  watch,
  errors,
  maxMarksInputRef,
  earnedMarksInputRef,
  maxGPATriggerRef,
  earnedGPATriggerRef,
  validationActive = false,
}) => {
  const currentGradeType = watch(
    `educations.${index}.gradeType`
  )?.toLowerCase();

  return (
    <div className="space-y-4 w-full">
      <div className="edu-score-toggle">
        {['MARKS', 'GPA'].map((type) => (
          <RadioButton
            key={type}
            id={`${type}-${index}`}
            value={type}
            checked={currentGradeType === type.toLowerCase()}
            onChange={(e) => onGradeTypeChange(e.target.value)}
            label={type === 'GPA' ? 'GPA' : 'Marks'}
          />
        ))}
      </div>
      <GradeInputs
        type={currentGradeType}
        index={index}
        control={control}
        watch={watch}
        errors={errors}
        maxMarksInputRef={maxMarksInputRef}
        earnedMarksInputRef={earnedMarksInputRef}
        maxGPATriggerRef={maxGPATriggerRef}
        earnedGPATriggerRef={earnedGPATriggerRef}
        validationActive={validationActive}
      />
    </div>
  );
};

// Main Component
export const EducationFields = ({
  index,
  control,
  watch,
  onRemove,
  errors,
  education,
  setValue,
  masterData,
  isLockedQualification = false,
  lockedQualificationType = null,
  validationActive = false,
  validationKey = 0,
}) => {
  const { toast } = useToast();
  const allEducations = watch('educations');
  const qualification = watch(`educations.${index}.qualification`);
  const formGradeType = watch(`educations.${index}.gradeType`);

  // Initialize gradeType from education prop first, then form value, default to 'marks'
  const initialGradeType =
    education?.gradeType?.toLowerCase() ||
    formGradeType?.toLowerCase() ||
    'marks';

  const [gradeType, setGradeType] = useState(initialGradeType);

  // Refs for focusing on error fields
  const institutionInputRef = useRef(null);
  const qualificationSelectRef = useRef(null);
  const yearSelectRef = useRef(null);
  const fieldOfStudySelectRef = useRef(null);
  const universityInputRef = useRef(null);
  const maxMarksInputRef = useRef(null);
  const earnedMarksInputRef = useRef(null);
  const maxGPATriggerRef = useRef(null);
  const earnedGPATriggerRef = useRef(null);

  // Move boardRef to top level to avoid conditional hook calls
  const boardRef = useRef(null);

  const firstErrorRowIndex = useMemo(
    () => getFirstEducationErrorRowIndex(errors?.educations),
    [errors]
  );

  // Set locked qualification on mount
  useEffect(() => {
    if (isLockedQualification && lockedQualificationType) {
      setValue(`educations.${index}.qualification`, lockedQualificationType);
    }
  }, [isLockedQualification, lockedQualificationType, index, setValue]);

  // Initialize gradeType in form if not already set, or sync with form value
  useEffect(() => {
    if (formGradeType) {
      // Sync state with form value (form value takes precedence)
      const normalized = formGradeType.toLowerCase();
      setGradeType((prev) => (prev !== normalized ? normalized : prev));
    } else if (education?.gradeType) {
      // If form doesn't have value but education prop does, set it
      const normalized = education.gradeType.toLowerCase();
      setValue(`educations.${index}.gradeType`, normalized.toUpperCase());
      setGradeType(normalized);
    } else if (!formGradeType && !education?.gradeType) {
      // Default to marks if nothing is set
      setValue(`educations.${index}.gradeType`, 'MARKS');
      setGradeType('marks');
    }
  }, [index, setValue, education?.gradeType, formGradeType]);

  // Check if this entry has any validation errors
  const hasErrors =
    errors?.educations?.[index]?.qualification ||
    errors?.educations?.[index]?.institution ||
    errors?.educations?.[index]?.graduationYear ||
    errors?.educations?.[index]?.fieldsOfStudy ||
    errors?.educations?.[index]?.board ||
    errors?.educations?.[index]?.university ||
    errors?.educations?.[index]?.maxMarks ||
    errors?.educations?.[index]?.earnedMarks ||
    errors?.educations?.[index]?.maxGPA ||
    errors?.educations?.[index]?.earnedGPA;

  // Helper function to scroll element into view within its scrollable container
  const scrollIntoViewInContainer = (element) => {
    if (!element) return;

    // Find the nearest scrollable parent (edit-form-body)
    let scrollContainer = element.closest('.edit-form-body');
    if (!scrollContainer) {
      // Fallback: find any scrollable parent
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          scrollContainer = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Calculate scroll position to center the element
      const scrollTop =
        scrollContainer.scrollTop +
        (elementRect.top - containerRect.top) -
        containerRect.height / 2 +
        elementRect.height / 2;

      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    } else {
      // Fallback to default scrollIntoView
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Track if we've already requested expansion for errors
  const hasRequestedExpansionRef = useRef(false);
  const previousHasErrorsRef = useRef(false);
  const lastAutoFocusValidationKeyRef = useRef(0);

  // Focus first invalid field on this row (top-to-bottom). Only the first row with errors
  // runs this so multiple EducationFields instances do not steal focus from each other.
  useEffect(() => {
    // Only auto-focus/scroll on explicit validation attempts (keyed by Save),
    // not on every keystroke while the user is typing.
    if (!validationActive) return;
    if (!validationKey) return;
    if (validationKey === lastAutoFocusValidationKeyRef.current) return;
    lastAutoFocusValidationKeyRef.current = validationKey;

    const errorsJustAppeared = hasErrors && !previousHasErrorsRef.current;
    const isFirstRowWithErrors = firstErrorRowIndex === index;

    if (
      errorsJustAppeared &&
      isFirstRowWithErrors &&
      !hasRequestedExpansionRef.current
    ) {
      hasRequestedExpansionRef.current = true;

      setTimeout(() => {
        const rowErrors = errors?.educations?.[index];
        const gradeType =
          watch(`educations.${index}.gradeType`)?.toLowerCase() || 'marks';

        const focusEl = (el) => {
          if (!el) return;
          el.focus();
          scrollIntoViewInContainer(el);
        };

        if (rowErrors?.qualification && qualificationSelectRef.current) {
          focusEl(qualificationSelectRef.current);
        } else if (rowErrors?.institution && institutionInputRef.current) {
          focusEl(institutionInputRef.current);
        } else if (rowErrors?.graduationYear && yearSelectRef.current) {
          focusEl(yearSelectRef.current);
        } else if (rowErrors?.fieldsOfStudy && fieldOfStudySelectRef.current) {
          focusEl(fieldOfStudySelectRef.current);
        } else if (rowErrors?.board && boardRef.current) {
          focusEl(boardRef.current);
        } else if (rowErrors?.university && universityInputRef.current) {
          focusEl(universityInputRef.current);
        } else if (gradeType === 'gpa') {
          if (rowErrors?.maxGPA && maxGPATriggerRef.current) {
            focusEl(maxGPATriggerRef.current);
          } else if (rowErrors?.earnedGPA && earnedGPATriggerRef.current) {
            focusEl(earnedGPATriggerRef.current);
          }
        } else if (rowErrors?.maxMarks && maxMarksInputRef.current) {
          focusEl(maxMarksInputRef.current);
        } else if (rowErrors?.earnedMarks && earnedMarksInputRef.current) {
          focusEl(earnedMarksInputRef.current);
        }
      }, 400);
    }

    if (!hasErrors && previousHasErrorsRef.current) {
      hasRequestedExpansionRef.current = false;
    }

    previousHasErrorsRef.current = hasErrors;
  }, [
    validationActive,
    validationKey,
    hasErrors,
    errors,
    index,
    firstErrorRowIndex,
    watch,
  ]);

  const handleGradeTypeChange = (newType) => {
    const normalizedType = newType.toLowerCase();
    setGradeType(normalizedType);
    setValue(`educations.${index}.gradeType`, normalizedType.toUpperCase());

    const clearFields =
      normalizedType === 'marks'
        ? ['maxGPA', 'earnedGPA']
        : ['maxMarks', 'earnedMarks'];

    clearFields.forEach((field) => {
      setValue(`educations.${index}.${field}`, '');
    });
  };

  const isSchoolLevel =
    qualification === 'TENTH' || qualification === 'TWELFTH';
  const isFieldOfStudyEnabled = [
    'TWELFTH',
    'DIPLOMA',
    'DEGREE',
    'MASTERS',
  ].includes(qualification);

  const handleQualificationChange = (value) => {
    const validationResult = validateNewQualification(
      allEducations,
      value,
      index
    );

    if (!validationResult.isValid) {
      toast({
        message: validationResult.message,
        variant: 'destructive',
      });
      return;
    }

    setValue(`educations.${index}.qualification`, value);
    // Reset board/university based on qualification
    if (value === 'TENTH' || value === 'TWELFTH') {
      setValue(`educations.${index}.university`, '');
    } else {
      setValue(`educations.${index}.board`, '');
    }
  };

  return (
    <div className="relative group space-y-4 text-left">
      {/* Qualification at the top */}
      {!isLockedQualification && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <FormSelect
              label="Qualification"
              required
              validationActive={validationActive}
              name={`educations.${index}.qualification`}
              control={control}
              options={educations.filter(
                (edu) => edu.value !== 'TENTH' && edu.value !== 'TWELFTH'
              )}
              placeholder="Select Qualification"
              data-field={`educations.${index}.qualification`}
              rules={{
                validate: (value, formValues) => {
                  const education = formValues.educations[index];
                  const hasAnyValue =
                    education.institution ||
                    education.qualification ||
                    education.graduationYear ||
                    education.fieldsOfStudy ||
                    education.board ||
                    education.university ||
                    education.maxMarks ||
                    education.earnedMarks ||
                    education.maxGPA ||
                    education.earnedGPA;

                  if (hasAnyValue && !value) {
                    return 'Please select a qualification';
                  }

                  const result = validateNewQualification(
                    allEducations,
                    value,
                    index
                  );
                  return result.isValid || result.message;
                },
              }}
              onChange={handleQualificationChange}
              id={`educations-${index}-qualification`}
              triggerRef={qualificationSelectRef}
            />
          </div>
        </div>
      )}

      {/* Institution - Full width */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <FormField
            label="Institution"
            id={`educations-${index}-institution`}
            errorMessage={errors?.educations?.[index]?.institution?.message}
            className="text-xs"
            required
            validationActive={validationActive}
            fieldHasValue={Boolean(
              watch(`educations.${index}.institution`)?.trim?.()
            )}
          >
            <Controller
              name={`educations.${index}.institution`}
              control={control}
              rules={{
                validate: (value, formValues) => {
                  const education = formValues.educations[index];
                  const hasAnyValue =
                    education.institution ||
                    education.qualification ||
                    education.graduationYear ||
                    education.fieldsOfStudy ||
                    education.board ||
                    education.university ||
                    education.maxMarks ||
                    education.earnedMarks ||
                    education.maxGPA ||
                    education.earnedGPA;

                  if (hasAnyValue && !value?.trim()) {
                    return 'Institution name is required';
                  }

                  return true;
                },
              }}
              data-field={`educations.${index}.institution`}
              render={({ field }) => (
                <Input
                  {...field}
                  ref={institutionInputRef}
                  id={`educations-${index}-institution`}
                  placeholder="Enter institution name"
                  className={`edu-fi text-xs ${
                    errors?.educations?.[index]?.institution
                      ? 'edu-fi--error'
                      : validationActive &&
                          Boolean(
                            watch(`educations.${index}.institution`)?.trim?.()
                          )
                        ? 'edu-fi--valid'
                        : ''
                  }`}
                />
              )}
            />
          </FormField>
        </div>
      </div>

      {/* Year of passing + Board (school) or Field of Study (degree) */}
      <div className="grid grid-cols-12 gap-4">
        <div
          className={`col-span-12 ${
            qualification === 'TENTH' ||
            qualification === 'TWELFTH' ||
            isFieldOfStudyEnabled
              ? 'md:col-span-6'
              : ''
          }`}
        >
          <FormSelect
            label="Year of Passing"
            required
            validationActive={validationActive}
            name={`educations.${index}.graduationYear`}
            control={control}
            options={generateYearOptions()}
            placeholder="Select year"
            icon={<CalendarIcon className="mr-2 h-4 w-4 shrink-0" />}
            data-field={`educations.${index}.graduationYear`}
            rules={{
              validate: (value, formValues) => {
                const education = formValues.educations[index];
                const hasAnyValue =
                  education.institution ||
                  education.qualification ||
                  education.graduationYear ||
                  education.fieldsOfStudy ||
                  education.board ||
                  education.university ||
                  education.maxMarks ||
                  education.earnedMarks ||
                  education.maxGPA ||
                  education.earnedGPA;

                if (hasAnyValue && !value) {
                  return 'Year is required';
                }

                return true;
              },
            }}
            id={`educations-${index}-graduationYear`}
            triggerRef={yearSelectRef}
          />
        </div>

        {qualification === 'TENTH' && (
          <div className="col-span-12 md:col-span-6">
            <FormSelect
              label="Board"
              required
              validationActive={validationActive}
              name={`educations.${index}.board`}
              control={control}
              options={boardOptions}
              placeholder="Select board"
              data-field={`educations.${index}.board`}
              rules={{
                validate: (value, formValues) => {
                  const education = formValues.educations[index];
                  const hasAnyValue =
                    education.institution ||
                    education.qualification ||
                    education.graduationYear ||
                    education.fieldsOfStudy ||
                    education.board ||
                    education.university ||
                    education.maxMarks ||
                    education.earnedMarks ||
                    education.maxGPA ||
                    education.earnedGPA;

                  if (hasAnyValue && isSchoolLevel && !value) {
                    return 'Board name is required';
                  }

                  return true;
                },
              }}
              id={`educations-${index}-board`}
              triggerRef={boardRef}
            />
          </div>
        )}

        {qualification === 'TWELFTH' && (
          <div className="col-span-12 md:col-span-6">
            <FormSelect
              label="Board"
              required
              validationActive={validationActive}
              name={`educations.${index}.board`}
              control={control}
              options={boardOptions}
              placeholder="Select board"
              data-field={`educations.${index}.board`}
              rules={{
                validate: (value, formValues) => {
                  const education = formValues.educations[index];
                  const hasAnyValue =
                    education.institution ||
                    education.qualification ||
                    education.graduationYear ||
                    education.fieldsOfStudy ||
                    education.board ||
                    education.university ||
                    education.maxMarks ||
                    education.earnedMarks ||
                    education.maxGPA ||
                    education.earnedGPA;

                  if (hasAnyValue && isSchoolLevel && !value) {
                    return 'Board name is required';
                  }

                  return true;
                },
              }}
              id={`educations-${index}-board`}
              triggerRef={boardRef}
            />
          </div>
        )}

        {qualification && isFieldOfStudyEnabled && (
          <div className="col-span-12 md:col-span-6">
            <FormSelect
              label="Field of Study"
              required
              validationActive={validationActive}
              name={`educations.${index}.fieldsOfStudy`}
              control={control}
              options={getFieldOfStudyOptions(
                qualification,
                isFieldOfStudyEnabled,
                masterData
              )}
              placeholder="Enter field of study"
              data-field={`educations.${index}.fieldsOfStudy`}
              rules={{
                validate: (value, formValues) => {
                  const education = formValues.educations[index];
                  const hasAnyValue =
                    education.institution ||
                    education.qualification ||
                    education.graduationYear ||
                    education.fieldsOfStudy ||
                    education.board ||
                    education.university ||
                    education.maxMarks ||
                    education.earnedMarks ||
                    education.maxGPA ||
                    education.earnedGPA;

                  // Mandatory for all entries except Secondary (TENTH)
                  if (hasAnyValue && qualification !== 'TENTH' && !value) {
                    return 'Field of Study is required';
                  }
                  return true;
                },
              }}
              id={`educations-${index}-fieldsOfStudy`}
              triggerRef={fieldOfStudySelectRef}
            />
          </div>
        )}
      </div>

      {qualification && !isSchoolLevel && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <FormField
              label="University"
              id={`educations-${index}-university`}
              errorMessage={errors?.educations?.[index]?.university?.message}
              className="text-xs"
              required
              validationActive={validationActive}
              fieldHasValue={Boolean(
                watch(`educations.${index}.university`)?.trim?.()
              )}
            >
              <Controller
                name={`educations.${index}.university`}
                control={control}
                data-field={`educations.${index}.university`}
                rules={{
                  validate: (value, formValues) => {
                    const education = formValues.educations[index];
                    const hasAnyValue =
                      education.institution ||
                      education.qualification ||
                      education.graduationYear ||
                      education.fieldsOfStudy ||
                      education.board ||
                      education.university ||
                      education.maxMarks ||
                      education.earnedMarks ||
                      education.maxGPA ||
                      education.earnedGPA;

                    // Mandatory for Bachelor's and Master's entries
                    const isDegreeLevel =
                      education.qualification === 'DEGREE' ||
                      education.qualification === 'MASTERS';
                    if (hasAnyValue && isDegreeLevel && !value?.trim?.()) {
                      return 'University is required';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    ref={universityInputRef}
                    placeholder="Enter university name"
                    id={`educations-${index}-university`}
                    className={`edu-fi text-xs ${
                      errors?.educations?.[index]?.university
                        ? 'edu-fi--error'
                        : validationActive &&
                            Boolean(
                              watch(`educations.${index}.university`)?.trim?.()
                            )
                          ? 'edu-fi--valid'
                          : ''
                    }`}
                  />
                )}
              />
            </FormField>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-12">
          <GradeTypeSelector
            index={index}
            onGradeTypeChange={handleGradeTypeChange}
            control={control}
            watch={watch}
            errors={errors}
            maxMarksInputRef={maxMarksInputRef}
            earnedMarksInputRef={earnedMarksInputRef}
            maxGPATriggerRef={maxGPATriggerRef}
            earnedGPATriggerRef={earnedGPATriggerRef}
            validationActive={validationActive}
          />
        </div>
      </div>
    </div>
  );
};

export const AddEducationButton = ({ onClick }) => (
  <AddButton onClick={onClick} text="Add Education" variant="add" />
);
